import {
  Alert,
  Box,
  ColumnLayout,
  ExpandableSection,
  FormField,
  Grid,
  Header,
  Input,
  Select,
  SpaceBetween,
  Table,
} from '@cloudscape-design/components';
import { useEffect, useState } from 'react';
import { useMedia } from 'react-use';

import type { ScoringConfigUpdate } from '../../common/api/scorecardThresholds';
import { CalendarPeriodControl } from '../../components/CalendarPeriodControl';
import { ScorecardGrade } from '../../components/ScorecardIdentity';
import { StatisticBox } from '../../components/StatisticBox';
import { calculateDraftScore, type ScoreCalculatorInput } from './calculator';

type CalculatorDraft = {
  averageRating: string;
  reviewCount: string;
  responseRate: string;
  responseTimeHours: string;
  periodDays: string;
  customerVolume: string;
  businessType: string;
  source: string;
};

const INITIAL_DRAFT: CalculatorDraft = {
  averageRating: '4.5',
  reviewCount: '15',
  responseRate: '80',
  responseTimeHours: '24',
  periodDays: '30',
  customerVolume: '1000',
  businessType: 'Other',
  source: 'google',
};

function numberValue(
  value: string,
  path: string,
  errors: Record<string, string>,
  min: number,
  max?: number,
): number {
  const parsed = Number(value);
  if (value.trim() === '' || !Number.isFinite(parsed)) {
    errors[path] = 'Enter a number.';
    return 0;
  }
  if (parsed < min) errors[path] = `Enter ${min} or more.`;
  if (max !== undefined && parsed > max) errors[path] = `Enter ${max} or less.`;
  return parsed;
}

function CalculatorField({
  label,
  value,
  unit,
  error,
  onChange,
}: {
  label: string;
  value: string;
  unit: string;
  error?: string;
  onChange: (value: string) => void;
}) {
  return (
    <FormField label={label} constraintText={unit} errorText={error}>
      <Input
        type="number"
        inputMode="decimal"
        value={value}
        invalid={Boolean(error)}
        onChange={({ detail }) => onChange(detail.value)}
      />
    </FormField>
  );
}

const percent = (value: number) => `${Number((value * 100).toFixed(2))}%`;

function statusCalculation(
  allocation: number,
  status: { extremely_low: number; below: number; above: number },
): string {
  if (allocation < status.extremely_low)
    return `${percent(allocation)} < ${percent(status.extremely_low)} extremely-low cutoff`;
  if (allocation < status.below)
    return `${percent(status.extremely_low)} ≤ ${percent(allocation)} < ${percent(status.below)}`;
  if (allocation >= status.above)
    return `${percent(allocation)} ≥ ${percent(status.above)} above-average cutoff`;
  return `${percent(status.below)} ≤ ${percent(allocation)} < ${percent(status.above)}`;
}

function criticalCalculation(value: number, cutoff: number, unit: string): string {
  if (cutoff < 0) return 'Critical cutoff disabled';
  const shownValue = unit.startsWith('%') ? `${value}${unit}` : `${value} ${unit}`;
  const shownCutoff = unit.startsWith('%') ? `${cutoff}${unit}` : `${cutoff} ${unit}`;
  return `${value <= cutoff ? 'Critical' : 'Not critical'}: ${shownValue} ${value <= cutoff ? '≤' : '>'} ${shownCutoff}`;
}

function ratingCriticalCalculation(value: number, cutoff: number): string {
  return `${value <= cutoff ? 'Critical' : 'Not critical'}: rating ${value} ${value <= cutoff ? '≤' : '>'} ${cutoff} stars`;
}

export function ThresholdCalculator({ config }: { config?: ScoringConfigUpdate }) {
  const compact = useMedia('(max-width: 767px)');
  const [draft, setDraft] = useState<CalculatorDraft>(INITIAL_DRAFT);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (!config || initialized) return;
    const businessType = Object.prototype.hasOwnProperty.call(
      config.review_volume.customer_volume_defaults,
      'Other',
    )
      ? 'Other'
      : Object.keys(config.review_volume.customer_volume_defaults)[0];
    const source = Object.prototype.hasOwnProperty.call(config.review_volume.source_map, 'google')
      ? 'google'
      : Object.keys(config.review_volume.source_map)[0];
    setDraft((current) => ({
      ...current,
      businessType,
      source,
      customerVolume: String(config.review_volume.customer_volume_defaults[businessType]),
    }));
    setInitialized(true);
  }, [config, initialized]);

  const update = (key: keyof CalculatorDraft, value: string) => {
    setDraft((current) => ({ ...current, [key]: value }));
  };
  const errors: Record<string, string> = {};
  const input: ScoreCalculatorInput = {
    averageRating: numberValue(draft.averageRating, 'averageRating', errors, 0, 5),
    reviewCount: numberValue(draft.reviewCount, 'reviewCount', errors, 0),
    responseRate: numberValue(draft.responseRate, 'responseRate', errors, 0, 100),
    responseTimeHours:
      draft.responseTimeHours.trim() === ''
        ? null
        : numberValue(draft.responseTimeHours, 'responseTimeHours', errors, 0),
    periodDays: numberValue(draft.periodDays, 'periodDays', errors, 1),
    customerVolume: numberValue(draft.customerVolume, 'customerVolume', errors, 1),
    businessType: draft.businessType,
    source: draft.source,
  };
  const result =
    config && Object.keys(errors).length === 0 ? calculateDraftScore(input, config) : null;
  const businessTypes = config ? Object.keys(config.review_volume.customer_volume_defaults) : [];
  const sources = config ? Object.keys(config.review_volume.source_map) : [];
  const effectiveSource = config
    ? (config.review_volume.source_map[input.source] ?? config.review_volume.unknown_source_default)
    : '';
  const effectiveBusinessType =
    config &&
    Object.prototype.hasOwnProperty.call(
      config.review_volume.customer_volume_defaults,
      input.businessType,
    )
      ? input.businessType
      : (config?.review_volume.unknown_business_type_default ?? '');
  const monthlyReviewPercent =
    input.periodDays > 0 && input.customerVolume > 0
      ? (((input.reviewCount / input.periodDays) * 30) / input.customerVolume) * 100
      : 0;
  const calculationRows =
    result && config
      ? [
          {
            metric: 'Review volume',
            observed: `${input.reviewCount} ÷ ${input.periodDays} days × 30 ÷ ${input.customerVolume} customers × 100 = ${Number(monthlyReviewPercent.toFixed(2))}%`,
            allocation: result.reviewVolume.allocation,
            status: result.reviewVolume.status,
            statusRules: config.review_volume.statuses[effectiveSource][effectiveBusinessType],
            weight: config.weights.review_count,
            critical: criticalCalculation(
              Number((result.reviewVolume.allocation * 100).toFixed(2)),
              config.review_volume.critical_thresholds[effectiveSource][effectiveBusinessType] *
                100,
              '% allocation',
            ),
          },
          {
            metric: 'Average rating',
            observed: `${input.averageRating} ${input.averageRating === 1 ? 'star' : 'stars'}`,
            allocation: result.averageRating.allocation,
            status: result.averageRating.status,
            statusRules: config.average_rating.status,
            weight: config.weights.average_rating,
            critical: ratingCriticalCalculation(
              input.averageRating,
              config.average_rating.critical_threshold,
            ),
          },
          {
            metric: 'Response rate',
            observed: `${input.responseRate}% of reviews answered`,
            allocation: result.responseRate.allocation,
            status: result.responseRate.status,
            statusRules: config.response_rate.status,
            weight: config.weights.response_rate,
            critical: criticalCalculation(
              Number((result.responseRate.allocation * 100).toFixed(2)),
              config.response_rate.critical_threshold * 100,
              '% allocation',
            ),
          },
          {
            metric: 'Response time',
            observed:
              input.responseTimeHours === null
                ? 'No response time available'
                : `${input.responseTimeHours} hours`,
            allocation: result.responseTime.allocation,
            status: result.responseTime.status,
            statusRules: config.response_time.status,
            weight: config.weights.response_time,
            critical: criticalCalculation(
              Number((result.responseTime.allocation * 100).toFixed(2)),
              config.response_time.critical_threshold * 100,
              '% allocation',
            ),
          },
        ]
      : [];
  const gradeThreshold = result && config ? config.grade_thresholds[result.grade] : undefined;

  return (
    <ExpandableSection
      variant="container"
      defaultExpanded
      headerText="Threshold calculator"
      headerDescription="Uses the current draft without saving it."
    >
      <SpaceBetween size="l">
        <Grid
          gridDefinition={[{ colspan: { default: 12, m: 5 } }, { colspan: { default: 12, m: 7 } }]}
        >
          <SpaceBetween size="m">
            <CalculatorField
              label="Monthly customers"
              unit="Customers"
              value={draft.customerVolume}
              error={errors.customerVolume}
              onChange={(value) => update('customerVolume', value)}
            />
            <ColumnLayout columns={2}>
              <FormField label="Review source">
                <Select
                  selectedOption={{ label: draft.source, value: draft.source }}
                  options={sources.map((source) => ({ label: source, value: source }))}
                  onChange={({ detail }) => update('source', detail.selectedOption.value ?? '')}
                />
              </FormField>
              <FormField label="Business type">
                <Select
                  selectedOption={{ label: draft.businessType, value: draft.businessType }}
                  options={businessTypes.map((businessType) => ({
                    label: businessType,
                    value: businessType,
                  }))}
                  onChange={({ detail }) => {
                    const businessType = detail.selectedOption.value ?? '';
                    setDraft((current) => ({
                      ...current,
                      businessType,
                      customerVolume: config
                        ? String(config.review_volume.customer_volume_defaults[businessType])
                        : current.customerVolume,
                    }));
                  }}
                />
              </FormField>
            </ColumnLayout>
            <Grid
              gridDefinition={[
                { colspan: { default: 12, m: 4 } },
                { colspan: { default: 12, m: 8 } },
              ]}
            >
              <CalculatorField
                label="Reviews"
                unit="Count in period"
                value={draft.reviewCount}
                error={errors.reviewCount}
                onChange={(value) => update('reviewCount', value)}
              />
              <CalendarPeriodControl
                label="Review period"
                selectedId={`${draft.periodDays}-days`}
                onChange={(selectedId) => update('periodDays', selectedId.replace('-days', ''))}
              />
            </Grid>
            <ColumnLayout columns={2}>
              <CalculatorField
                label="Average rating"
                unit="Stars"
                value={draft.averageRating}
                error={errors.averageRating}
                onChange={(value) => update('averageRating', value)}
              />
              <CalculatorField
                label="Response rate"
                unit="Percent (%)"
                value={draft.responseRate}
                error={errors.responseRate}
                onChange={(value) => update('responseRate', value)}
              />
            </ColumnLayout>
            <CalculatorField
              label="Response time"
              unit="Hours · blank if unavailable"
              value={draft.responseTimeHours}
              error={errors.responseTimeHours}
              onChange={(value) => update('responseTimeHours', value)}
            />
          </SpaceBetween>
          <SpaceBetween size="m">
            {!config ? (
              <Alert type="warning">Fix threshold errors to restore the preview.</Alert>
            ) : null}
            {result ? (
              <>
                <div className="flex items-center gap-4">
                  <ScorecardGrade grade={result.grade} compact />
                  <Header
                    variant="h3"
                    description={`${(result.score * 100).toFixed(1)} weighted score`}
                  >
                    Draft scorecard
                  </Header>
                </div>
                {result.critical ? (
                  <Box color="text-status-error" fontWeight="bold">
                    Critical cutoff reached
                  </Box>
                ) : null}
                <Grid
                  gridDefinition={[
                    { colspan: { default: 6, m: 3 } },
                    { colspan: { default: 6, m: 3 } },
                    { colspan: { default: 6, m: 3 } },
                    { colspan: { default: 6, m: 3 } },
                  ]}
                >
                  <StatisticBox
                    compact
                    label="Reviews"
                    icon="contact"
                    statistic={input.reviewCount.toLocaleString()}
                    details={`${((input.reviewCount / input.periodDays) * 30).toFixed(0)} / month`}
                    percentile={result.reviewVolume.allocation}
                    status={result.reviewVolume.status}
                  />
                  <StatisticBox
                    compact
                    label="Average Rating"
                    icon="star"
                    statistic={input.averageRating.toFixed(1)}
                    percentile={result.averageRating.allocation}
                    status={result.averageRating.status}
                  />
                  <StatisticBox
                    compact
                    label="Response Rate"
                    icon="undo"
                    statistic={`${input.responseRate.toFixed(0)}%`}
                    percentile={result.responseRate.allocation}
                    status={result.responseRate.status}
                  />
                  <StatisticBox
                    compact
                    label="Response Time"
                    icon="calendar"
                    statistic={
                      input.responseTimeHours === null ? 'N/A' : `${input.responseTimeHours} hr`
                    }
                    percentile={result.responseTime.allocation}
                    status={result.responseTime.status}
                  />
                </Grid>
              </>
            ) : null}
          </SpaceBetween>
        </Grid>
        {result && config ? (
          <Table
            variant="embedded"
            wrapLines
            header={
              <Header
                variant="h3"
                description={
                  result.critical
                    ? `A critical cutoff overrides the ${percent(result.score)} weighted score.`
                    : `${calculationRows.map((row) => percent(row.allocation * row.weight)).join(' + ')} = ${percent(result.score)}. ${result.grade} starts at ${gradeThreshold === undefined ? 'the fallback' : percent(gradeThreshold)}.`
                }
              >
                How this grade is calculated
              </Header>
            }
            items={calculationRows}
            columnDefinitions={
              compact
                ? [
                    {
                      id: 'calculation-mobile',
                      header: 'Calculation',
                      cell: (row) => (
                        <SpaceBetween size="xxs">
                          <Box fontWeight="bold">{row.metric}</Box>
                          <Box>{row.observed}</Box>
                          <Box>
                            {percent(row.allocation)} allocation × {percent(row.weight)} weight ={' '}
                            {percent(row.allocation * row.weight)}
                          </Box>
                          <Box>{statusCalculation(row.allocation, row.statusRules)}</Box>
                          <Box
                            color={
                              row.critical.startsWith('Critical:') ? 'text-status-error' : undefined
                            }
                          >
                            {row.critical}
                          </Box>
                        </SpaceBetween>
                      ),
                    },
                  ]
                : [
                    {
                      id: 'metric',
                      header: 'Metric and observed value',
                      cell: (row) => (
                        <SpaceBetween size="xxs">
                          <Box fontWeight="bold">{row.metric}</Box>
                          <Box>{row.observed}</Box>
                        </SpaceBetween>
                      ),
                    },
                    {
                      id: 'points',
                      header: 'Allocation and weighted points',
                      cell: (row) => (
                        <SpaceBetween size="xxs">
                          <Box>{percent(row.allocation)} allocation</Box>
                          <Box>
                            {percent(row.allocation)} × {percent(row.weight)} weight ={' '}
                            {percent(row.allocation * row.weight)}
                          </Box>
                        </SpaceBetween>
                      ),
                    },
                    {
                      id: 'result',
                      header: 'Status and critical rule',
                      cell: (row) => (
                        <SpaceBetween size="xxs">
                          <Box fontWeight="bold">{row.status.replaceAll('-', ' ')}</Box>
                          <Box>{statusCalculation(row.allocation, row.statusRules)}</Box>
                          <Box
                            color={
                              row.critical.startsWith('Critical:') ? 'text-status-error' : undefined
                            }
                          >
                            {row.critical}
                          </Box>
                        </SpaceBetween>
                      ),
                    },
                  ]
            }
          />
        ) : null}
      </SpaceBetween>
    </ExpandableSection>
  );
}
